import React from 'react';
import { Link } from 'react-router-dom';
import { useRecentlyViewed } from '../../context/RecentlyViewedContext';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';

const RecentlyViewed = ({ showTitle = true, maxItems = 5 }) => {
  const { recentlyViewed, removeFromRecentlyViewed, clearRecentlyViewed } = useRecentlyViewed();
  const { addToCart, isInCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();

  if (recentlyViewed.length === 0) return null;

  const displayItems = recentlyViewed.slice(0, maxItems);

  const handleAddToCart = (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
  };

  const handleWishlistToggle = (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isInWishlist(product.id)) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(product);
    }
  };

  const handleRemove = (productId, e) => {
    e.preventDefault();
    e.stopPropagation();
    removeFromRecentlyViewed(productId);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header */}
      {showTitle && (
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Recently Viewed</h3>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">
              {recentlyViewed.length} item{recentlyViewed.length !== 1 ? 's' : ''}
            </span>
            {recentlyViewed.length > 0 && (
              <button
                onClick={clearRecentlyViewed}
                className="text-xs text-red-600 hover:text-red-700 font-medium"
              >
                Clear All
              </button>
            )}
          </div>
        </div>
      )}

      {/* Products Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {displayItems.map(product => (
          <Link
            key={product.id}
            to={`/product/${product.id}`}
            className="group relative bg-gray-50 rounded-lg overflow-hidden hover:shadow-md transition-all duration-300"
          >
            {/* Remove Button */}
            <button
              onClick={(e) => handleRemove(product.id, e)}
              className="absolute top-2 right-2 z-10 p-1 bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 hover:text-red-600"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Product Image */}
            <div className="relative overflow-hidden">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-300"
              />
              
              {/* Quick Actions Overlay */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  {/* Add to Cart */}
                  <button
                    onClick={(e) => handleAddToCart(product, e)}
                    className={`p-2 rounded-full shadow-lg transition-all transform hover:scale-110 ${
                      isInCart(product.id)
                        ? 'bg-green-500 text-white'
                        : 'bg-white text-gray-700 hover:bg-blue-500 hover:text-white'
                    }`}
                    title={isInCart(product.id) ? 'In Cart' : 'Add to Cart'}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5L17 18a2 2 0 100 4 2 2 0 000-4m-8 4a2 2 0 100 4 2 2 0 000-4" />
                    </svg>
                  </button>

                  {/* Add to Wishlist */}
                  <button
                    onClick={(e) => handleWishlistToggle(product, e)}
                    className={`p-2 rounded-full shadow-lg transition-all transform hover:scale-110 ${
                      isInWishlist(product.id)
                        ? 'bg-red-500 text-white'
                        : 'bg-white text-gray-700 hover:bg-red-500 hover:text-white'
                    }`}
                    title={isInWishlist(product.id) ? 'Remove from Wishlist' : 'Add to Wishlist'}
                  >
                    <svg className="w-4 h-4" fill={isInWishlist(product.id) ? "currentColor" : "none"} stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Product Info */}
            <div className="p-3">
              <h4 className="font-medium text-gray-900 text-sm truncate mb-1">
                {product.name}
              </h4>
              <p className="text-xs text-gray-600 mb-2">{product.category}</p>
              <div className="flex items-center justify-between">
                <span className="font-bold text-blue-600">${product.price.toFixed(2)}</span>
                
                {/* Rating */}
                <div className="flex items-center gap-1">
                  <svg className="w-3 h-3 text-yellow-400 fill-current" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                  <span className="text-xs text-gray-600">{product.rating}</span>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* View All Link */}
      {recentlyViewed.length > maxItems && (
        <div className="mt-4 text-center">
          <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
            View All {recentlyViewed.length} Recently Viewed Items →
          </button>
        </div>
      )}
    </div>
  );
};

export default RecentlyViewed;
